"use client";

import * as React from "react";

import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Command as CommandPrimitive } from "cmdk";
import { CheckIcon, ChevronDownIcon, XIcon } from "lucide-react";

import { cn } from "../../utils";
import { Badge, badgeVariants } from "./badge";
import { Button } from "./button";
import { InputGroup, InputGroupAddon, InputGroupButton } from "./input-group";
import { Kbd } from "./kbd";

export interface ComboboxOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

interface ComboboxContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  value: string | string[];
  onValueChange: (value: string | string[]) => void;
  inputValue: string;
  setInputValue: (value: string) => void;
  multiple: boolean;
  disabled: boolean;
  items: string[];
  markJustSelected: () => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onSearch?: (searchValue: string) => void;
  minSearchLength: number;
  isSearching: boolean;
  disableClientFiltering: boolean;
}

const ComboboxContext = React.createContext<ComboboxContextValue | null>(null);

function useCombobox() {
  const ctx = React.useContext(ComboboxContext);
  if (!ctx) throw new Error("Combobox components must be used within Combobox");
  return ctx;
}

const ComboboxContentContext = React.createContext(false);

interface ChipsOverflowContextValue {
  showAll: boolean;
  setShowAll: (show: boolean) => void;
}
const ChipsOverflowContext =
  React.createContext<ChipsOverflowContextValue | null>(null);

interface ComboboxProps {
  value?: string | string[];
  defaultValue?: string | string[];
  onValueChange?: (value: string | string[]) => void;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  inputValue?: string;
  defaultInputValue?: string;
  onInputValueChange?: (value: string) => void;
  multiple?: boolean;
  disabled?: boolean;
  children?: React.ReactNode;
  filter?: (value: string, search: string, keywords?: string[]) => number;
  items?: string[];
  onSearch?: (searchValue: string) => void;
  minSearchLength?: number;
  isSearching?: boolean;
  disableClientFiltering?: boolean;
}

function Combobox({
  value: valueProp,
  defaultValue,
  onValueChange,
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  inputValue: inputValueProp,
  defaultInputValue = "",
  onInputValueChange,
  multiple = false,
  disabled = false,
  filter,
  items = [],
  onSearch,
  minSearchLength = 0,
  isSearching = false,
  disableClientFiltering = false,
  children,
}: ComboboxProps) {
  const [openState, setOpenState] = React.useState(defaultOpen);
  const open = openProp ?? openState;

  const [inputValueState, setInputValueState] =
    React.useState(defaultInputValue);
  const inputValue = inputValueProp ?? inputValueState;

  const [valueState, setValueState] = React.useState<string | string[]>(
    defaultValue ?? (multiple ? [] : ""),
  );
  const value = valueProp ?? valueState;

  const justSelectedRef = React.useRef(false);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const handleValueChange = React.useCallback(
    (next: string | string[]) => {
      setValueState(next);
      onValueChange?.(next);
    },
    [onValueChange],
  );

  const setInputValue = React.useCallback(
    (next: string) => {
      setInputValueState(next);
      onInputValueChange?.(next);
      if (next === "" && !multiple && typeof value === "string" && value) {
        handleValueChange("");
      }
    },
    [multiple, value, onInputValueChange, handleValueChange],
  );

  const setOpen = React.useCallback(
    (next: boolean) => {
      justSelectedRef.current = false;
      setOpenState(next);
      onOpenChange?.(next);
    },
    [multiple, value, onOpenChange, setInputValue],
  );

  const markJustSelected = React.useCallback(() => {
    justSelectedRef.current = true;
  }, []);

  const effectiveFilter = React.useCallback(
    (itemValue: string, search: string, keywords?: string[]) => {
      if (search === "") return 1;
      if (!multiple && typeof value === "string" && search === value) return 1;
      if (filter) return filter(itemValue, search, keywords);
      return itemValue.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
    },
    [multiple, value, filter],
  );

  React.useEffect(() => {
    if (onSearch && inputValue.length >= minSearchLength) {
      onSearch(inputValue);
    }
  }, [inputValue, minSearchLength, onSearch]);

  const shouldFilter = !disableClientFiltering;

  return (
    <ComboboxContext
      value={{
        open,
        setOpen,
        value,
        onValueChange: handleValueChange,
        inputValue,
        setInputValue,
        multiple,
        disabled,
        items,
        markJustSelected,
        inputRef,
        onSearch,
        minSearchLength,
        isSearching,
        disableClientFiltering,
      }}
    >
      <CommandPrimitive filter={effectiveFilter} shouldFilter={shouldFilter}>
        <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
          {children}
        </PopoverPrimitive.Root>
      </CommandPrimitive>
    </ComboboxContext>
  );
}

interface ComboboxValueProps {
  placeholder?: string;
  className?: string;
  children?: React.ReactNode | ((value: string | string[]) => React.ReactNode);
}

function ComboboxValue({
  placeholder,
  className,
  children,
}: ComboboxValueProps) {
  const { value } = useCombobox();

  if (typeof children === "function") return <>{children(value)}</>;
  if (children !== undefined) return <>{children}</>;

  const text = Array.isArray(value)
    ? value.join(", ") || placeholder
    : value || placeholder;

  return (
    <span data-slot="combobox-value" className={className}>
      {text}
    </span>
  );
}

function ComboboxTrigger({
  className,
  children,
  ref,
  asChild = false,
  ...props
}: React.ComponentPropsWithRef<typeof PopoverPrimitive.Trigger>) {
  return (
    <PopoverPrimitive.Trigger
      ref={ref}
      asChild={asChild}
      data-slot="combobox-trigger"
      className={cn("[&_svg:not([class*='af-size-'])]:af-size-4", className)}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {children}
          <ChevronDownIcon className="af-pointer-events-none af-size-4 af-text-muted-foreground" />
        </>
      )}
    </PopoverPrimitive.Trigger>
  );
}
ComboboxTrigger.displayName = "ComboboxTrigger";

function ComboboxClear({
  className,
  onClick,
  ref,
  ...props
}: Omit<React.ComponentPropsWithRef<typeof InputGroupButton>, "size">) {
  const { onValueChange, multiple, setInputValue } = useCombobox();

  return (
    <InputGroupButton
      ref={ref}
      data-slot="combobox-clear"
      variant="ghost"
      aria-label="Clear"
      className={cn(className)}
      onClick={(e) => {
        onValueChange(multiple ? [] : "");
        setInputValue("");
        onClick?.(e);
      }}
      {...props}
    >
      <XIcon className="af-pointer-events-none" />
    </InputGroupButton>
  );
}
ComboboxClear.displayName = "ComboboxClear";

interface ComboboxInputProps extends Omit<
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>,
  "value" | "onValueChange"
> {
  showTrigger?: boolean;
  showClear?: boolean;
  showShortcutHint?: boolean;
  openOnFocus?: boolean;
}

function ComboboxInput({
  className,
  children,
  disabled: disabledProp,
  showTrigger = true,
  showClear = false,
  showShortcutHint = false,
  openOnFocus = true,
  placeholder,
  onFocus,
  onKeyDown,
  ...props
}: ComboboxInputProps) {
  const {
    inputValue,
    setInputValue,
    setOpen,
    open,
    value,
    multiple,
    disabled: ctxDisabled,
    inputRef: ctxInputRef,
  } = useCombobox();
  const insideContent = React.useContext(ComboboxContentContext);
  const isDisabled = disabledProp ?? ctxDisabled;
  const localRef = React.useRef<HTMLInputElement>(null);
  const composedRef = React.useCallback(
    (node: HTMLInputElement | null) => {
      localRef.current = node;
      ctxInputRef.current = node;
    },
    [ctxInputRef],
  );

  if (insideContent) {
    return (
      <CommandPrimitive.Input
        ref={composedRef}
        autoFocus
        data-slot="combobox-input"
        value={inputValue}
        placeholder={placeholder}
        onValueChange={setInputValue}
        disabled={isDisabled}
        className={cn(
          "af-h-9 af-w-full af-border-b af-border-input af-bg-transparent af-px-3 af-py-2 af-text-sm placeholder:af-text-muted-foreground focus:af-outline-none disabled:af-cursor-not-allowed",
          className,
        )}
        {...props}
      />
    );
  }

  return (
    <PopoverPrimitive.Anchor asChild>
      <InputGroup data-combobox-anchor className={cn("af-w-auto", className)}>
        <CommandPrimitive.Input
          ref={composedRef}
          data-slot="input-group-control"
          value={inputValue}
          placeholder={placeholder}
          onValueChange={(next) => {
            setInputValue(next);
            if (!open && !openOnFocus) {
              setOpen(true);
            }
          }}
          onFocus={(event) => {
            onFocus?.(event);
            if (event.defaultPrevented) return;

            if (openOnFocus && !open) {
              if (!multiple && typeof value === "string" && value) {
                setInputValue(inputValue);
              }
              setOpen(true);
            }
          }}
          onKeyDown={(event) => {
            onKeyDown?.(event);
            if (event.defaultPrevented) return;

            if (
              !open &&
              !openOnFocus &&
              ["Enter", "ArrowDown"].includes(event.key)
            ) {
              event.preventDefault();
              if (!multiple && typeof value === "string" && value) {
                setInputValue(inputValue);
              }
              setOpen(true);
            }
          }}
          disabled={isDisabled}
          className="af-h-full af-min-w-0 af-flex-1 af-rounded-none af-border-0 af-bg-transparent af-px-3 af-py-2 af-text-sm af-shadow-none placeholder:af-text-muted-foreground focus:af-outline-none focus:af-ring-0 focus-visible:af-ring-0 focus-visible:af-ring-offset-0 disabled:af-cursor-not-allowed dark:af-bg-transparent"
          {...props}
        />
        <InputGroupAddon align="inline-end">
          {showShortcutHint && (
            <Kbd className="af-mr-1 af-rounded-full">
              {navigator.platform.includes("Mac") ? "⌘F" : "Ctrl+F"}
            </Kbd>
          )}
          {showClear && <ComboboxClear disabled={isDisabled} />}
          {showTrigger && (
            <InputGroupButton
              variant="ghost"
              data-slot="input-group-button"
              disabled={isDisabled}
              onPointerDown={(e) => e.preventDefault()}
              onClick={() => {
                if (open) {
                  setOpen(false);
                } else {
                  localRef.current?.focus();
                }
              }}
            >
              <ChevronDownIcon className="af-pointer-events-none" />
            </InputGroupButton>
          )}
        </InputGroupAddon>
        {children}
      </InputGroup>
    </PopoverPrimitive.Anchor>
  );
}

interface ComboboxContentProps extends Omit<
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>,
  "align" | "side"
> {
  side?: "top" | "bottom" | "left" | "right";
  sideOffset?: number;
  align?: "start" | "center" | "end";
  alignOffset?: number;
}

function ComboboxContent({
  className,
  side = "bottom",
  sideOffset = 6,
  align = "start",
  alignOffset = 0,
  children,
  ...props
}: ComboboxContentProps) {
  return (
    <ComboboxContentContext value={true}>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          data-slot="combobox-content"
          side={side}
          sideOffset={sideOffset}
          align={align}
          alignOffset={alignOffset}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onFocusOutside={(e) => {
            const target = e.target;
            if (
              target instanceof HTMLElement &&
              target.closest("[data-combobox-anchor]")
            ) {
              e.preventDefault();
            }
          }}
          onPointerDownOutside={(e) => {
            const target = e.target;
            if (
              target instanceof HTMLElement &&
              target.closest("[data-combobox-anchor]")
            ) {
              e.preventDefault();
            }
          }}
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          className={cn(
            "af-relative af-z-50 af-overflow-hidden af-rounded-lg af-bg-popover af-text-popover-foreground af-shadow-md af-ring-1 af-ring-foreground/10 af-duration-100",
            "data-[state=open]:af-animate-in data-[state=closed]:af-animate-out",
            "data-[state=closed]:af-fade-out-0 data-[state=open]:af-fade-in-0",
            "data-[state=closed]:af-zoom-out-95 data-[state=open]:af-zoom-in-95",
            "data-[side=bottom]:af-slide-in-from-top-2 data-[side=left]:af-slide-in-from-right-2 data-[side=right]:af-slide-in-from-left-2 data-[side=top]:af-slide-in-from-bottom-2",
            "af-min-w-[var(--radix-popover-anchor-width,var(--radix-popover-trigger-width))]",
            className,
          )}
          {...props}
        >
          {children}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </ComboboxContentContext>
  );
}

type ComboboxListChildren =
  | React.ReactNode
  | ((item: string) => React.ReactNode);

interface ComboboxListProps extends Omit<
  React.ComponentPropsWithRef<typeof CommandPrimitive.List>,
  "children"
> {
  children?: ComboboxListChildren;
}

function ComboboxList({
  className,
  children,
  ref,
  ...props
}: ComboboxListProps) {
  const { items } = useCombobox();
  const rendered =
    typeof children === "function" ? items.map(children) : children;

  return (
    <CommandPrimitive.List
      ref={ref}
      data-slot="combobox-list"
      className={cn(
        "af-max-h-[min(18rem,var(--radix-popover-content-available-height,18rem))] af-scroll-py-1 af-overflow-y-auto af-overscroll-contain af-p-1",
        className,
      )}
      {...props}
    >
      {rendered}
    </CommandPrimitive.List>
  );
}
ComboboxList.displayName = "ComboboxList";

interface ComboboxItemProps extends React.ComponentPropsWithoutRef<
  typeof CommandPrimitive.Item
> {
  value: string;
  hideCheck?: boolean;
}

function ComboboxItem({
  className,
  children,
  value: itemValue,
  onSelect: onSelectProp,
  hideCheck = false,
  ...props
}: ComboboxItemProps) {
  const {
    value,
    onValueChange,
    multiple,
    setOpen,
    setInputValue,
    markJustSelected,
  } = useCombobox();

  const isSelected = React.useMemo(
    () =>
      Array.isArray(value) ? value.includes(itemValue) : value === itemValue,
    [value, itemValue],
  );

  const handleSelect = React.useCallback(
    (selectedValue: string) => {
      if (multiple) {
        const arr = Array.isArray(value) ? value : [];
        onValueChange(
          arr.includes(selectedValue)
            ? arr.filter((v) => v !== selectedValue)
            : [...arr, selectedValue],
        );
        setInputValue("");
      } else {
        const next = selectedValue === value ? "" : selectedValue;
        onValueChange(next);
        setInputValue(next);
        markJustSelected();
        setOpen(false);
      }
      onSelectProp?.(selectedValue);
    },
    [
      value,
      multiple,
      onValueChange,
      setOpen,
      setInputValue,
      markJustSelected,
      onSelectProp,
    ],
  );

  return (
    <CommandPrimitive.Item
      data-slot="combobox-item"
      className={cn(
        "af-relative af-flex af-w-full af-cursor-default af-select-none af-items-center af-gap-2 af-rounded-md af-py-1 af-pl-1.5 af-pr-8 af-text-sm af-outline-none",
        "data-[selected=true]:af-bg-accent data-[selected=true]:af-text-accent-foreground",
        "data-[disabled=true]:af-pointer-events-none data-[disabled=true]:af-opacity-50",
        "[&_svg:not([class*='af-size-'])]:af-size-4 [&_svg]:af-pointer-events-none [&_svg]:af-shrink-0",
        className,
      )}
      value={itemValue}
      onSelect={handleSelect}
      {...props}
    >
      {children}
      {!hideCheck && isSelected && (
        <span className="af-pointer-events-none af-absolute af-right-2 af-flex af-size-4 af-items-center af-justify-center">
          <CheckIcon className="af-pointer-events-none" />
        </span>
      )}
    </CommandPrimitive.Item>
  );
}

function ComboboxGroup({
  className,
  ref,
  ...props
}: React.ComponentPropsWithRef<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      ref={ref}
      data-slot="combobox-group"
      className={cn(className)}
      {...props}
    />
  );
}
ComboboxGroup.displayName = "ComboboxGroup";

function ComboboxLabel({
  className,
  ...props
}: React.ComponentPropsWithoutRef<"div">) {
  return (
    <div
      data-slot="combobox-label"
      className={cn(
        "af-px-2 af-py-1.5 af-text-xs af-text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}

function ComboboxCollection({ children }: { children?: React.ReactNode }) {
  return <>{children}</>;
}

function ComboboxEmpty({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>) {
  return (
    <CommandPrimitive.Empty
      data-slot="combobox-empty"
      className={cn(
        "af-py-6 af-text-center af-text-sm af-text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}

function ComboboxSeparator({
  className,
  ref,
  ...props
}: React.ComponentPropsWithRef<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator
      ref={ref}
      data-slot="combobox-separator"
      className={cn("af--mx-1 af-my-1 af-h-px af-bg-border", className)}
      {...props}
    />
  );
}
ComboboxSeparator.displayName = "ComboboxSeparator";

function ComboboxChips({
  className,
  ref,
  children,
  onClick,
  ...props
}: React.ComponentPropsWithRef<"div">) {
  const { setOpen, disabled, inputRef } = useCombobox();
  const [showOverflowAll, setShowOverflowAll] = React.useState(false);

  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (disabled) return;
      inputRef.current?.focus();
      setOpen(true);
      onClick?.(e);
    },
    [disabled, inputRef, setOpen, onClick],
  );

  return (
    <ChipsOverflowContext.Provider
      value={{ showAll: showOverflowAll, setShowAll: setShowOverflowAll }}
    >
      <PopoverPrimitive.Anchor asChild>
        <div
          ref={ref}
          data-combobox-anchor
          data-slot="combobox-chips"
          className={cn(
            "focus-within:af-ring-3 af-relative af-flex af-min-h-8 af-items-center af-gap-2 af-rounded-lg af-border af-border-input af-bg-transparent af-bg-clip-padding af-px-3 af-py-2.5 af-text-sm af-transition-colors focus-within:af-border-ring focus-within:af-ring-ring/50 dark:af-bg-input/30",
            !disabled && "af-cursor-text",
            className,
          )}
          onClick={handleClick}
          {...props}
        >
          {children}
        </div>
      </PopoverPrimitive.Anchor>
    </ChipsOverflowContext.Provider>
  );
}
ComboboxChips.displayName = "ComboboxChips";

function ComboboxChipsContent({
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<"div">) {
  return (
    <div
      data-slot="combobox-chips-content"
      className={cn(
        "af-flex af-min-w-0 af-flex-1 af-flex-wrap af-items-center af-gap-1",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}
ComboboxChipsContent.displayName = "ComboboxChipsContent";

interface ComboboxChipsClearProps extends React.ComponentPropsWithoutRef<
  typeof Button
> {
  showWhenEmpty?: boolean;
}

function ComboboxChipsClear({
  className,
  onClick,
  showWhenEmpty = false,
  ...props
}: ComboboxChipsClearProps) {
  const { onValueChange, multiple, setInputValue, value, disabled } =
    useCombobox();

  const hasValue =
    (Array.isArray(value) && value.length > 0) ||
    (!Array.isArray(value) && !!value);

  if (!hasValue && !showWhenEmpty) return null;

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-xs"
      data-slot="combobox-chips-clear"
      className={cn(
        "af-flex af-h-6 af-w-6 af-items-center af-justify-center af-rounded-full",
        className,
      )}
      disabled={disabled}
      onClick={(event) => {
        onValueChange(multiple ? [] : "");
        setInputValue("");
        onClick?.(event);
      }}
      {...props}
    >
      <XIcon className="af-pointer-events-none af-size-4" />
    </Button>
  );
}

interface ComboboxChipProps extends React.ComponentPropsWithoutRef<"div"> {
  value?: string;
  showRemove?: boolean;
}

function ComboboxChip({
  className,
  children,
  value: valueProp,
  showRemove = true,
  ...props
}: ComboboxChipProps) {
  const { value, onValueChange, disabled } = useCombobox();
  const chipValue = valueProp ?? (typeof children === "string" ? children : "");

  const handleRemove = React.useCallback(() => {
    if (Array.isArray(value)) {
      onValueChange(value.filter((v) => v !== chipValue));
    }
  }, [value, chipValue, onValueChange]);

  return (
    <Badge
      data-slot="combobox-chip"
      variant="outline"
      className={cn("af-flex af-items-center af-gap-1", className)}
      {...props}
    >
      {showRemove && !disabled && (
        <button
          type="button"
          disabled={disabled}
          data-slot="combobox-chip-remove"
          onClick={handleRemove}
        >
          <XIcon className="af-pointer-events-none af-size-3" />
        </button>
      )}
      {children}
    </Badge>
  );
}

interface ComboboxChipsOverflowProps {
  maxVisible?: number;
  renderChip: (value: string) => React.ReactNode;
}

function ComboboxChipsOverflow({
  maxVisible = 3,
  renderChip,
}: ComboboxChipsOverflowProps) {
  const { value, inputRef } = useCombobox();
  const chipsCtx = React.useContext(ChipsOverflowContext);
  const [localShowAll, setLocalShowAll] = React.useState(false);

  const showAll = chipsCtx?.showAll ?? localShowAll;
  const setShowAll = chipsCtx?.setShowAll ?? setLocalShowAll;

  const handleShowAll = React.useCallback(() => {
    setShowAll(true);
    inputRef.current?.focus();
  }, [setShowAll, inputRef]);

  const values = Array.isArray(value) ? value : [];
  if (!values.length) return null;

  const hiddenCount = Math.max(0, values.length - maxVisible);
  const visibleValues =
    showAll || hiddenCount === 0 ? values : values.slice(0, maxVisible);

  return (
    <>
      {visibleValues.map((v) => renderChip(v))}
      {!showAll && hiddenCount > 0 && (
        <button
          type="button"
          data-slot="combobox-chips-overflow"
          className={cn(badgeVariants({ variant: "secondary" }))}
          onClick={handleShowAll}
        >
          + {hiddenCount} more
        </button>
      )}
    </>
  );
}

function toSlug(s: string) {
  return s.toLowerCase().replace(/\s+/g, "-");
}

interface ComboboxChipsInputProps extends Omit<
  React.ComponentPropsWithRef<typeof CommandPrimitive.Input>,
  "value" | "onValueChange"
> {
  creatable?: boolean;
}

function ComboboxChipsInput({
  className,
  onKeyDown,
  onPaste,
  onBlur,
  creatable = false,
  ref,
  ...props
}: ComboboxChipsInputProps) {
  const {
    inputValue,
    setInputValue,
    setOpen,
    value,
    onValueChange,
    disabled,
    inputRef,
  } = useCombobox();
  const chipsCtx = React.useContext(ChipsOverflowContext);

  const composedRef = React.useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    },
    [ref, inputRef],
  );

  const latest = React.useRef({
    inputValue,
    value,
    onValueChange,
    setInputValue,
    creatable,
    onKeyDown,
    onPaste,
  });
  latest.current = {
    inputValue,
    value,
    onValueChange,
    setInputValue,
    creatable,
    onKeyDown,
    onPaste,
  };

  const handleKeyDown = React.useCallback<
    React.KeyboardEventHandler<HTMLInputElement>
  >((e) => {
    const {
      inputValue,
      value,
      onValueChange,
      setInputValue,
      creatable,
      onKeyDown,
    } = latest.current;
    const arr = Array.isArray(value) ? value : null;

    if (
      (e.key === "Backspace" || e.key === "Delete") &&
      inputValue === "" &&
      arr?.length
    ) {
      onValueChange(arr.slice(0, -1));
    } else if (e.key === "Escape") {
      e.currentTarget.blur();
    } else if (e.key === "," && creatable) {
      e.preventDefault();
      const slug = toSlug(inputValue.trim());
      if (slug && arr && !arr.includes(slug)) {
        onValueChange([...arr, slug]);
      }
      setInputValue("");
    }

    onKeyDown?.(e);
  }, []);

  const handlePaste = React.useCallback<
    React.ClipboardEventHandler<HTMLInputElement>
  >((e) => {
    const { value, onValueChange, setInputValue, creatable, onPaste } =
      latest.current;
    const arr = Array.isArray(value) ? value : null;

    if (!creatable || !arr) {
      onPaste?.(e);
      return;
    }

    const incoming = e.clipboardData
      .getData("text")
      .split(",")
      .map((p) => toSlug(p.trim()))
      .filter((slug) => slug && !arr.includes(slug));

    if (!incoming.length) {
      onPaste?.(e);
      return;
    }

    e.preventDefault();
    onValueChange([...arr, ...incoming]);
    setInputValue("");
  }, []);

  const handleBlur = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      chipsCtx?.setShowAll(false);
      onBlur?.(e);
    },
    [chipsCtx, onBlur],
  );

  return (
    <CommandPrimitive.Input
      ref={composedRef}
      data-slot="combobox-chip-input"
      value={inputValue}
      onValueChange={setInputValue}
      onFocus={() => setOpen(true)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      disabled={disabled}
      className={cn(
        "af-min-w-16 af-flex-1 af-bg-transparent focus:af-outline-none disabled:af-cursor-not-allowed",
        className,
      )}
      {...props}
    />
  );
}
ComboboxChipsInput.displayName = "ComboboxChipsInput";

function useComboboxAnchor() {
  return React.useRef<HTMLDivElement | null>(null);
}

export {
  Combobox,
  ComboboxInput,
  ComboboxContent,
  ComboboxList,
  ComboboxItem,
  ComboboxGroup,
  ComboboxLabel,
  ComboboxCollection,
  ComboboxEmpty,
  ComboboxSeparator,
  ComboboxChips,
  ComboboxChipsContent,
  ComboboxChip,
  ComboboxChipsClear,
  ComboboxChipsOverflow,
  ComboboxChipsInput,
  ComboboxTrigger,
  ComboboxValue,
  useComboboxAnchor,
  useCombobox,
};
